import React, { useMemo, useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    PageHeader,
    FlexRowBetween,
    FlexRow,
    AppTable,
} from 'components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { DesktopMode, MobileMode } from 'layout'
import { useAuth } from 'context/user'
import { getAssetPermissions } from 'utility/asset-permissions'
import { useNotification } from 'hooks/toast'
import { Input, Modal, Form, Checkbox, Message } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { CaretLeft, X } from 'phosphor-react'
import styled from 'styled-components'

const PageWrap = styled.div`
    padding: 20px;
    margin-top: 10px;
`

const Breadcrumb = styled.div`
    font-size: 13px;
    color: #64748b;
    margin-bottom: 8px;
    span.pointer {
        cursor: pointer;
        &:hover {
            text-decoration: underline;
        }
    }
`

const Toolbar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
    margin-bottom: 16px;
`

const ToolbarField = styled.div`
    min-width: 160px;
    flex: 1 1 160px;
    label {
        display: block;
        font-size: 12px;
        font-weight: 600;
        margin-bottom: 6px;
        color: #334155;
    }
`

/** Mock until backend provides list + link APIs (EAUDITOR-489–492). */
const MOCK_TEMPLATES = [
    {
        Id: 't1',
        name: 'Safety Check',
        category: 'Safety',
        status: 'Active',
        linkedAssetIds: [],
    },
    {
        Id: 't2',
        name: 'Equipment Maintenance',
        category: 'Maintenance',
        status: 'Active',
        linkedAssetIds: ['a1', 'a2', 'a3'],
    },
    {
        Id: 't3',
        name: 'Fire Safety Inspection',
        category: 'Safety',
        status: 'Draft',
        linkedAssetIds: ['a4'],
    },
    {
        Id: 't4',
        name: 'Fleet Walkaround',
        category: 'Fleet',
        status: 'Active',
        linkedAssetIds: [],
    },
    {
        Id: 't5',
        name: 'IT Asset Review',
        category: 'IT',
        status: 'Inactive',
        linkedAssetIds: [],
    },
]

const MOCK_ASSETS = [
    { id: 'a1', name: 'Compressor Unit A', category: 'Maintenance' },
    { id: 'a2', name: 'HVAC Roof Pack', category: 'Maintenance' },
    { id: 'a3', name: 'Forklift #12', category: 'Fleet' },
    { id: 'a4', name: 'Server Rack 01', category: 'IT' },
    { id: 'a5', name: 'Sprinkler Zone B', category: 'Safety' },
    { id: 'a6', name: 'Emergency Exit East', category: 'Safety' },
]

const ALL_TEMPLATES_OPTION = { key: 'all', text: 'All templates', value: '' }
const ALL_CATEGORIES_OPTION = { key: 'all', text: 'All categories', value: '' }
const ALL_STATUSES_OPTION = { key: 'all', text: 'All statuses', value: '' }

export const AssetTemplateLinkingScreen = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const permissions = getAssetPermissions(user)
    const { successToast } = useNotification()

    const [rows, setRows] = useState(MOCK_TEMPLATES)
    const [searchText, setSearchText] = useState('')
    const [templateFilter, setTemplateFilter] = useState('')
    const [categoryFilter, setCategoryFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('')

    const [linkModalOpen, setLinkModalOpen] = useState(false)
    const [activeTemplate, setActiveTemplate] = useState(null)
    const [selectedAssetIds, setSelectedAssetIds] = useState({})

    const [bulkModalOpen, setBulkModalOpen] = useState(false)
    const [bulkTemplateId, setBulkTemplateId] = useState('')
    const [bulkCategory, setBulkCategory] = useState('')
    const [bulkSelectedIds, setBulkSelectedIds] = useState({})

    const [confirmOpen, setConfirmOpen] = useState(false)
    const [confirmMessage, setConfirmMessage] = useState('')
    const confirmHandlerRef = useRef(null)

    const canMutate = permissions.canEditAsset && !permissions.isViewOnly

    useEffect(() => {
        if (!permissions.canViewAssetModule) {
            navigate('/app/asset-management', { replace: true })
        }
    }, [permissions.canViewAssetModule, navigate])

    const categoryOptions = useMemo(() => {
        const set = new Set(MOCK_TEMPLATES.map(t => t.category))
        return [
            ALL_CATEGORIES_OPTION,
            ...[...set].sort().map(c => ({ key: c, text: c, value: c })),
        ]
    }, [])

    const templateFilterOptions = useMemo(
        () => [
            ALL_TEMPLATES_OPTION,
            ...MOCK_TEMPLATES.map(t => ({
                key: t.Id,
                text: t.name,
                value: t.Id,
            })),
        ],
        [],
    )

    const statusOptions = useMemo(
        () => [
            ALL_STATUSES_OPTION,
            { key: 'Active', text: 'Active', value: 'Active' },
            { key: 'Draft', text: 'Draft', value: 'Draft' },
            { key: 'Inactive', text: 'Inactive', value: 'Inactive' },
        ],
        [],
    )

    const bulkTemplateOptions = useMemo(
        () =>
            MOCK_TEMPLATES.map(t => ({
                key: t.Id,
                text: t.name,
                value: t.Id,
            })),
        [],
    )

    const filteredRows = useMemo(() => {
        return rows.filter(row => {
            if (templateFilter && row.Id !== templateFilter) return false
            if (categoryFilter && row.category !== categoryFilter) return false
            if (statusFilter && row.status !== statusFilter) return false
            if (searchText) {
                const q = searchText.trim().toLowerCase()
                if (
                    !row.name.toLowerCase().includes(q) &&
                    !row.category.toLowerCase().includes(q)
                ) {
                    return false
                }
            }
            return true
        })
    }, [rows, templateFilter, categoryFilter, statusFilter, searchText])

    const openLinkModal = template => {
        if (!canMutate) return
        setActiveTemplate(template)
        const initial = {}
        MOCK_ASSETS.forEach(a => {
            initial[a.id] = template.linkedAssetIds.includes(a.id)
        })
        setSelectedAssetIds(initial)
        setLinkModalOpen(true)
    }

    const toggleAsset = assetId => {
        setSelectedAssetIds(prev => ({
            ...prev,
            [assetId]: !prev[assetId],
        }))
    }

    const requestApplyLinks = () => {
        if (!activeTemplate || !canMutate) return
        const picked = Object.entries(selectedAssetIds)
            .filter(([, v]) => v)
            .map(([k]) => k)
        setConfirmMessage(
            `Apply ${picked.length} asset link(s) for template "${activeTemplate.name}"? (Mock only until API is ready.)`,
        )
        confirmHandlerRef.current = () => {
            setRows(prev =>
                prev.map(r =>
                    r.Id === activeTemplate.Id
                        ? { ...r, linkedAssetIds: picked }
                        : r,
                ),
            )
            successToast('Links saved locally (mock). Backend API pending.')
            setLinkModalOpen(false)
            setActiveTemplate(null)
        }
        setConfirmOpen(true)
    }

    const unlinkTemplate = template => {
        if (!canMutate) return
        setConfirmMessage(
            `Remove all asset links from "${template.name}"? (Mock only until API is ready.)`,
        )
        confirmHandlerRef.current = () => {
            setRows(prev =>
                prev.map(r =>
                    r.Id === template.Id ? { ...r, linkedAssetIds: [] } : r,
                ),
            )
            successToast('Links cleared locally (mock). Backend API pending.')
        }
        setConfirmOpen(true)
    }

    const openBulkModal = () => {
        if (!canMutate) return
        setBulkTemplateId(MOCK_TEMPLATES[0]?.Id || '')
        setBulkCategory('')
        setBulkSelectedIds({})
        setBulkModalOpen(true)
    }

    const bulkAssets = useMemo(() => {
        if (!bulkCategory) return []
        return MOCK_ASSETS.filter(a => a.category === bulkCategory)
    }, [bulkCategory])

    const toggleBulkAsset = id => {
        setBulkSelectedIds(prev => ({ ...prev, [id]: !prev[id] }))
    }

    const requestBulkApply = () => {
        if (!bulkTemplateId || !bulkCategory || !canMutate) return
        const picked = Object.entries(bulkSelectedIds)
            .filter(([, v]) => v)
            .map(([k]) => k)
        const tpl = rows.find(r => r.Id === bulkTemplateId)
        setConfirmMessage(
            `Bulk link ${picked.length} asset(s) in category "${bulkCategory}" to "${tpl?.name || 'template'}"? (Mock only until API is ready.)`,
        )
        confirmHandlerRef.current = () => {
            setRows(prev =>
                prev.map(r =>
                    r.Id === bulkTemplateId
                        ? { ...r, linkedAssetIds: picked }
                        : r,
                ),
            )
            successToast('Bulk links saved locally (mock). Backend API pending.')
            setBulkModalOpen(false)
        }
        setConfirmOpen(true)
    }

    const linkedLabel = row => {
        if (!row.linkedAssetIds?.length) return 'Not linked'
        const n = row.linkedAssetIds.length
        return `${n} Asset${n === 1 ? '' : 's'}`
    }

    const runConfirm = () => {
        const fn = confirmHandlerRef.current
        confirmHandlerRef.current = null
        if (typeof fn === 'function') {
            fn()
        }
        setConfirmOpen(false)
    }

    const columns = [
        { title: 'Template Name', key: 'name', textAlign: 'left' },
        { title: 'Category', key: 'category', textAlign: 'left' },
        {
            title: 'Linked Assets',
            key: 'linked',
            textAlign: 'left',
            render: item => linkedLabel(item),
        },
        {
            title: 'Actions',
            key: 'actions',
            textAlign: 'right',
            render: item => (
                <div className="d-flex justify-content-end gap-2 flex-wrap">
                    <AppSecondaryButton
                        type="button"
                        disabled={!canMutate}
                        onClick={() => openLinkModal(item)}
                    >
                        Link
                    </AppSecondaryButton>
                    <AppSecondaryButton
                        type="button"
                        disabled={
                            !canMutate || !item.linkedAssetIds?.length
                        }
                        onClick={() => unlinkTemplate(item)}
                    >
                        Unlink
                    </AppSecondaryButton>
                </div>
            ),
        },
    ]

    return (
        <PageWrap>
            <DesktopMode>
                <FlexRow className="align-items-center mb-2 pointer" onClick={() => navigate('/app/asset-management')}>
                    <CaretLeft size={22} />
                    <span className="ms-2">Back to Assets</span>
                </FlexRow>
                <Breadcrumb>
                    <span
                        className="pointer"
                        onClick={() => navigate('/app/asset-management')}
                    >
                        Asset Management
                    </span>
                    <span> &gt; </span>
                    <span>Linking Interface</span>
                </Breadcrumb>
                <FlexRowBetween className="mb-3 align-items-end flex-wrap gap-2">
                    <PageHeader header="Inspection Templates" />
                    <AppPrimaryButton
                        type="button"
                        onClick={openBulkModal}
                        disabled={!canMutate}
                    >
                        Link assets (bulk)
                    </AppPrimaryButton>
                </FlexRowBetween>
            </DesktopMode>

            <MobileMode>
                <FlexRow className="align-items-center mb-2 pointer" onClick={() => navigate('/app/asset-management')}>
                    <CaretLeft size={22} />
                    <span className="ms-2">Back</span>
                </FlexRow>
                <PageHeader header="Inspection Templates" />
                <AppPrimaryButton
                    type="button"
                    fluid
                    className="mb-3"
                    onClick={openBulkModal}
                    disabled={!canMutate}
                >
                    Link assets (bulk)
                </AppPrimaryButton>
            </MobileMode>

            {!permissions.canViewAssetModule ? null : (
                <>
                    {!canMutate && (
                        <Message info>
                            You have view-only access. Link and unlink actions are disabled.
                        </Message>
                    )}

                    <Toolbar>
                        <ToolbarField style={{ flex: '2 1 220px' }}>
                            <label htmlFor="tpl-search">Search</label>
                            <Input
                                id="tpl-search"
                                icon="search"
                                iconPosition="left"
                                placeholder="Search templates"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                            />
                        </ToolbarField>
                        <ToolbarField>
                            <label>Template</label>
                            <Dropdown
                                selection
                                fluid
                                options={templateFilterOptions}
                                value={templateFilter}
                                onChange={(_, { value }) =>
                                    setTemplateFilter(value || '')
                                }
                            />
                        </ToolbarField>
                        <ToolbarField>
                            <label>Category</label>
                            <Dropdown
                                selection
                                fluid
                                options={categoryOptions}
                                value={categoryFilter}
                                onChange={(_, { value }) =>
                                    setCategoryFilter(value || '')
                                }
                            />
                        </ToolbarField>
                        <ToolbarField>
                            <label>Status</label>
                            <Dropdown
                                selection
                                fluid
                                options={statusOptions}
                                value={statusFilter}
                                onChange={(_, { value }) =>
                                    setStatusFilter(value || '')
                                }
                            />
                        </ToolbarField>
                    </Toolbar>

                    <AppTable
                        columns={columns}
                        data={filteredRows}
                        hidePagination
                    />
                </>
            )}

            <Modal
                open={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                size="small"
            >
                <Modal.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <span>Link</span>
                        <X
                            className="pointer"
                            size={22}
                            onClick={() => setLinkModalOpen(false)}
                        />
                    </div>
                </Modal.Header>
                <Modal.Content scrolling>
                    <p>
                        Link &apos;{activeTemplate?.name}&apos; template to the following
                        assets:
                    </p>
                    <Form>
                        {MOCK_ASSETS.map(asset => (
                            <Form.Field key={asset.id}>
                                <Checkbox
                                    label={`${asset.name} (${asset.category})`}
                                    checked={!!selectedAssetIds[asset.id]}
                                    onChange={() => toggleAsset(asset.id)}
                                    disabled={!canMutate}
                                />
                            </Form.Field>
                        ))}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setLinkModalOpen(false)}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton
                        type="button"
                        onClick={requestApplyLinks}
                        disabled={!canMutate}
                    >
                        Apply link
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>

            <Modal
                open={bulkModalOpen}
                onClose={() => setBulkModalOpen(false)}
                size="small"
            >
                <Modal.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <span>Bulk link by category</span>
                        <X
                            className="pointer"
                            size={22}
                            onClick={() => setBulkModalOpen(false)}
                        />
                    </div>
                </Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Field>
                            <label>Template</label>
                            <Dropdown
                                selection
                                fluid
                                options={bulkTemplateOptions}
                                value={bulkTemplateId}
                                onChange={(_, { value }) =>
                                    setBulkTemplateId(value || '')
                                }
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Category</label>
                            <Dropdown
                                selection
                                fluid
                                placeholder="Select category"
                                options={categoryOptions.filter(
                                    o => o.value !== '',
                                )}
                                value={bulkCategory}
                                onChange={(_, { value }) => {
                                    setBulkCategory(value || '')
                                    setBulkSelectedIds({})
                                }}
                            />
                        </Form.Field>
                        {bulkCategory ? (
                            <>
                                <p className="mt-2 mb-2" style={{ fontSize: 13 }}>
                                    Assets in this category (mock list):
                                </p>
                                {bulkAssets.length === 0 ? (
                                    <Message>No mock assets for this category.</Message>
                                ) : (
                                    bulkAssets.map(asset => (
                                        <Form.Field key={asset.id}>
                                            <Checkbox
                                                label={asset.name}
                                                checked={!!bulkSelectedIds[asset.id]}
                                                onChange={() =>
                                                    toggleBulkAsset(asset.id)
                                                }
                                                disabled={!canMutate}
                                            />
                                        </Form.Field>
                                    ))
                                )}
                            </>
                        ) : (
                            <Message>Select a category to see assets.</Message>
                        )}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setBulkModalOpen(false)}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton
                        type="button"
                        onClick={requestBulkApply}
                        disabled={
                            !canMutate ||
                            !bulkTemplateId ||
                            !bulkCategory ||
                            !Object.values(bulkSelectedIds).some(Boolean)
                        }
                    >
                        Apply bulk link
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>

            <Modal
                open={confirmOpen}
                size="tiny"
                onClose={() => {
                    confirmHandlerRef.current = null
                    setConfirmOpen(false)
                }}
            >
                <Modal.Header>Confirm</Modal.Header>
                <Modal.Content>
                    <p>{confirmMessage}</p>
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => {
                            confirmHandlerRef.current = null
                            setConfirmOpen(false)
                        }}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton type="button" onClick={runConfirm}>
                        Confirm
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </PageWrap>
    )
}
